import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Network, Plus, Pencil, Trash2, ChevronRight, ChevronDown,
  ArrowLeft, Lock, Grid3x3, GitBranch, Map
} from 'lucide-react';

// Physical view status colors
export const PHYS_STATUS = {
  free:       { bg: 'bg-slate-100 hover:bg-emerald-50 border-slate-200', label: 'Free', dot: 'bg-slate-300' },
  assigned:   { bg: 'bg-indigo-100 border-indigo-300 text-indigo-700', label: 'Assigned', dot: 'bg-indigo-500' },
  autoblocked:{ bg: 'bg-amber-100 border-amber-300 text-amber-700', label: 'Autoblocked', dot: 'bg-amber-500' },
  reserved:   { bg: 'bg-amber-100 border-amber-300 text-amber-700', label: 'Reserved', dot: 'bg-amber-500' },
  blocked:    { bg: 'bg-red-100 border-red-300 text-red-700', label: 'Blocked', dot: 'bg-red-500' },
  network:    { bg: 'bg-slate-200 border-slate-300 text-slate-600', label: 'Network', dot: 'bg-slate-500' },
  broadcast:  { bg: 'bg-slate-200 border-slate-300 text-slate-600', label: 'Broadcast', dot: 'bg-slate-500' },
  gateway:    { bg: 'bg-purple-100 border-purple-300 text-purple-700', label: 'Gateway', dot: 'bg-purple-500' },
  partial:    { bg: 'bg-yellow-50 border-yellow-300 text-yellow-700', label: 'Partial', dot: 'bg-yellow-500' },
  active:     { bg: 'bg-emerald-100 border-emerald-300 text-emerald-700', label: 'Active', dot: 'bg-emerald-500' },
  dhcp:       { bg: 'bg-blue-100 border-blue-300 text-blue-700', label: 'DHCP', dot: 'bg-blue-500' },
  offline:    { bg: 'bg-red-50 border-red-200 text-red-600', label: 'Offline', dot: 'bg-red-400' },
  available:  { bg: 'bg-slate-100 border-slate-200', label: 'Available', dot: 'bg-slate-300' },
};

export function UsageBar({ usage }) {
  const pct = usage?.usage_percent || 0;
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{usage?.used || 0} used / {usage?.usable_hosts || 0} total</span><span>{pct}%</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

export function SubnetTreeRow({ node, depth, expanded, setExpanded, onOpen, onEdit, onDelete }) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded[node.subnet_id] !== false;
  const pct = node.usage?.usage_percent || 0;
  const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const isBlocked = node.is_blocked;

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 border-l-2 ${isBlocked ? 'border-red-400 bg-red-50/30' : 'border-transparent hover:border-indigo-300'}`}
      style={{ paddingLeft: `${depth * 20 + 8}px` }}
      data-testid={`tree-node-${node.subnet_id}`}
    >
      <button
        className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700"
        onClick={() => setExpanded(prev => ({ ...prev, [node.subnet_id]: !isOpen }))}
        disabled={!hasChildren}
      >
        {hasChildren ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-3 h-3 rounded-full bg-slate-200" />}
      </button>
      <div className={`p-1 rounded ${isBlocked ? 'bg-red-100' : node.version === 6 ? 'bg-purple-100' : 'bg-emerald-100'}`}>
        {isBlocked ? <Lock className="h-3.5 w-3.5 text-red-600" /> : <Network className={`h-3.5 w-3.5 ${node.version === 6 ? 'text-purple-600' : 'text-emerald-600'}`} />}
      </div>
      <button
        className="flex-1 text-left cursor-pointer min-w-0"
        onClick={() => !isBlocked && onOpen(node)}
        disabled={isBlocked}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-mono font-semibold text-sm ${isBlocked ? 'text-red-700 line-through' : 'text-slate-800'}`}>{node.subnet}</span>
          {node.display_status && (() => {
            const m = {
              assigned: 'bg-indigo-100 text-indigo-700 border-indigo-300',
              autoblocked: 'bg-amber-100 text-amber-700 border-amber-300',
              partial: 'bg-sky-100 text-sky-700 border-sky-300',
              reserved: 'bg-slate-100 text-slate-600 border-slate-300',
              free: 'bg-emerald-100 text-emerald-700 border-emerald-300',
            };
            const labels = { partial: 'Partially Allocated', free: 'Available', assigned: 'Allocated' };
            const ds = node.display_status;
            const label = labels[ds] || (ds.charAt(0).toUpperCase() + ds.slice(1));
            const title = ds === 'partial' ? 'Contains one or more allocated child subnets' : ds === 'autoblocked' ? 'Inside an allocated block' : '';
            return <Badge title={title} className={`text-[9px] h-4 px-1 ${m[ds] || m.free}`} data-testid={`tree-status-${node.subnet_id}`}>{label}</Badge>;
          })()}
          {node.version === 6 && <Badge variant="outline" className="text-[9px] h-4 px-1 text-purple-700 border-purple-300">IPv6</Badge>}
          {isBlocked && <Badge className="bg-red-100 text-red-700 border-red-300 text-[9px] h-4 px-1">BLOCKED {node.block_reason === '/31_pair' ? '/31 pair' : ''}</Badge>}
          {node.is_pool && !isBlocked && <Badge variant="outline" className="text-[9px] h-4 px-1">Pool</Badge>}
          {node.vlan_id && <Badge variant="outline" className="text-[9px] h-4 px-1">VLAN {node.vlan_id}</Badge>}
          {node.customer_name && <span className="text-xs text-indigo-600 font-medium truncate">{node.customer_name}</span>}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {node.description || (isBlocked ? 'Sibling auto-blocked by /31 pair rule' : '—')}
          {node.gateway ? ` • GW: ${node.gateway}` : ''}
        </div>
      </button>
      {!isBlocked && (
        <div className="w-24 hidden md:block">
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">{node.usage?.used || 0}/{(node.usage?.usable_hosts || 0).toLocaleString()}</div>
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); onEdit(node); }}><Pencil className="h-3 w-3 text-amber-600" /></Button>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); onDelete(node); }}><Trash2 className="h-3 w-3 text-red-500" /></Button>
      </div>
    </div>
  );
}

// Flatten tree into {node, depth} list respecting expanded state (iterative, no recursion)
export function flattenTree(tree, expanded) {
  const out = [];
  const stack = tree.slice().reverse().map(n => ({ node: n, depth: 0 }));
  while (stack.length) {
    const { node, depth } = stack.pop();
    out.push({ node, depth });
    const isOpen = expanded[node.subnet_id] !== false;
    if (isOpen && node.children && node.children.length) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push({ node: node.children[i], depth: depth + 1 });
      }
    }
  }
  return out;
}

export function SubnetTreeList({ tree, expanded, setExpanded, onOpen, onEdit, onDelete }) {
  const flat = flattenTree(tree, expanded);
  return (
    <div className="space-y-1">
      {flat.map(({ node, depth }) => (
        <SubnetTreeRow
          key={node.subnet_id}
          node={node}
          depth={depth}
          expanded={expanded}
          setExpanded={setExpanded}
          onOpen={onOpen}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function PhysicalIpTile({ it }) {
  const st = PHYS_STATUS[it.status] || PHYS_STATUS.free;
  const last = (it.ip || '').split('.').pop();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={`h-6 rounded border text-[9px] font-mono truncate px-0.5 transition-all ${st.bg}`}
          data-testid={`phys-ip-${(it.ip || '').replace(/\./g, '-')}`}
        >
          {last}
        </button>
      </TooltipTrigger>
      <TooltipContent className="text-xs">
        <p className="font-mono font-bold">{it.ip}</p>
        <p>{st.label}{it.block_reason === '/31_pair' ? ' (/31 pair)' : ''}</p>
        {it.hostname && <p>Host: {it.hostname}</p>}
        {it.description && <p>{it.description}</p>}
        {it.customer_name && <p className="text-indigo-300">{it.customer_name}</p>}
        {it.child_subnet && <p className="text-emerald-300 font-mono">{it.child_subnet}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

function PhysicalBlockTile({ it, onDrillDown }) {
  const st = PHYS_STATUS[it.status] || PHYS_STATUS.free;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={`p-2 rounded-md border text-xs font-mono ${st.bg} text-left transition-all`}
          onClick={() => it.child_subnet_id && onDrillDown(it.child_subnet_id)}
          data-testid={`phys-block-${(it.subnet || '').replace(/[/.:]/g, '-')}`}
        >
          <div className="font-semibold truncate">{it.subnet}</div>
          <div className="text-[10px] opacity-70 mt-0.5">{st.label}{it.block_reason === '/31_pair' ? ' (/31)' : ''}</div>
          {it.partial_count > 0 && (
            <div className="text-[10px] opacity-70 mt-0.5">{it.assigned_count}A · {it.blocked_count}B · {it.partial_count}T</div>
          )}
          {it.customer_name && <div className="text-[10px] text-indigo-600 truncate mt-0.5">{it.customer_name}</div>}
        </button>
      </TooltipTrigger>
      <TooltipContent className="text-xs">
        <p className="font-mono font-bold">{it.subnet}</p>
        <p>{st.label}{it.block_reason === '/31_pair' ? ' (/31 pair)' : ''}</p>
        {it.description && <p>{it.description}</p>}
        {it.customer_name && <p className="text-indigo-300">{it.customer_name}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

export function PhysicalViewGrid({ data, onDrillDown }) {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading...</div>;
  const items = data.items || [];
  const isIpGrid = data.mode === 'ip_grid';
  const legend = ['assigned', 'reserved', 'blocked', 'free', 'partial', 'network', 'broadcast', 'gateway'];

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 text-xs">
          {legend.map(s => (
            <span key={s} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${PHYS_STATUS[s].dot}`} />{PHYS_STATUS[s].label}</span>
          ))}
        </div>
        {data.mode === 'block_grid' && (
          <p className="text-xs text-slate-500">
            Showing {items.length} sub-blocks at <span className="font-mono font-semibold text-indigo-600">/{data.aggregation_prefix}</span>
            {data.total_blocks > items.length ? ` of ${data.total_blocks.toLocaleString()} total` : ''}. Click a block to drill down.
          </p>
        )}
        <div className={`grid gap-1 ${isIpGrid ? 'grid-cols-8 sm:grid-cols-12 md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'}`}>
          {items.map((it, i) => (
            isIpGrid
              ? <PhysicalIpTile key={it.ip || i} it={it} />
              : <PhysicalBlockTile key={it.subnet || i} it={it} onDrillDown={onDrillDown} />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}

function ChildSubnetRow({ ch, openChild, handleDeleteSubnet }) {
  return (
    <div className={`flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 ${ch.is_blocked ? 'border-red-200 bg-red-50/30' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded ${ch.is_blocked ? 'bg-red-100' : 'bg-indigo-100'}`}>
          {ch.is_blocked ? <Lock className="h-4 w-4 text-red-600" /> : <Network className="h-4 w-4 text-indigo-600" />}
        </div>
        <div>
          <p className={`font-mono font-semibold text-sm ${ch.is_blocked ? 'text-red-700 line-through' : ''}`}>
            {ch.subnet}
            {ch.is_blocked && <Badge className="ml-2 bg-red-100 text-red-700 border-red-300 text-[9px]">BLOCKED /31 pair</Badge>}
          </p>
          <p className="text-xs text-slate-500">
            {ch.description || '-'}
            {ch.vlan_id ? ` | VLAN ${ch.vlan_id}` : ''}
            {ch.gateway ? ` | GW: ${ch.gateway}` : ''}
            {ch.customer_id && ch.customer ? ` | ${ch.customer?.company_name}` : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!ch.is_blocked && <div className="w-32"><UsageBar usage={ch.usage} /></div>}
        {!ch.is_blocked && <Button variant="ghost" size="sm" onClick={() => openChild(ch.subnet_id)}><ChevronRight className="h-4 w-4" /></Button>}
        <Button variant="ghost" size="sm" onClick={() => handleDeleteSubnet(ch.subnet_id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
      </div>
    </div>
  );
}

function ChildSubnetsCard({ childList, openChild, handleDeleteSubnet }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Created Sub-Subnets ({childList.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-2">
          {childList.map(ch => (
            <ChildSubnetRow key={ch.subnet_id} ch={ch} openChild={openChild} handleDeleteSubnet={handleDeleteSubnet} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SplitTile({ sn, opt, subnetAddresses, setSplitForm, setShowSplitCreate }) {
  const isExisting = opt.existing.find(e => e.subnet === sn);
  const isBlocked = isExisting?.is_blocked;
  const tileClass = isBlocked
    ? 'bg-red-50 border-red-300 text-red-700 cursor-not-allowed line-through'
    : isExisting
      ? 'bg-indigo-100 border-indigo-300 text-indigo-700 cursor-default'
      : 'bg-white border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 cursor-pointer hover:shadow-sm';
  const label = isBlocked
    ? '/31 pair blocked'
    : isExisting
      ? (isExisting.description || 'In use')
      : `${opt.usable_per_subnet > 1e15 ? '~unlimited' : opt.usable_per_subnet.toLocaleString()} hosts`;
  return (
    <button
      onClick={() => {
        if (!isExisting) {
          setSplitForm({ child_subnet: sn, description: '', vlan_id: '', gateway: '', customer_id: '', location: '', is_pool: false });
          setShowSplitCreate(subnetAddresses.subnet);
        }
      }}
      className={`px-3 py-2 rounded-lg border text-xs font-mono transition-all ${tileClass}`}
      data-testid={`split-tile-${sn.replace(/[/.:]/g, '-')}`}
    >
      <div className="font-semibold flex items-center gap-1">{isBlocked && <Lock className="h-3 w-3" />}{sn}</div>
      <div className="text-[10px] mt-0.5 opacity-70">{label}</div>
    </button>
  );
}

function SplitOptionRow({ opt, subnetAddresses, setSplitForm, setShowSplitCreate }) {
  const ipv6Labels = { 48: 'Site', 56: 'Subnet Block', 64: 'LAN/Subnet', 112: 'Interface', 128: 'Host' };
  const prefixNote = ipv6Labels[opt.prefix] ? ` (${ipv6Labels[opt.prefix]})` : '';
  const freeLabel = opt.available_count > 1e6 ? `${(opt.available_count / 1e6).toFixed(0)}M+` : opt.available_count;
  const hostsLabel = opt.usable_per_subnet > 1e15 ? '~unlimited' : opt.usable_per_subnet.toLocaleString();
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono font-bold text-indigo-700">/{opt.prefix}{prefixNote}</span>
        <span className="text-xs text-slate-500">{opt.label} · {hostsLabel} hosts each</span>
        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{freeLabel} free</Badge>
        {opt.existing_count > 0 && <Badge variant="outline" className="text-[10px]">{opt.existing_count} created</Badge>}
      </div>
      {opt.too_large_to_enumerate ? (
        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-400">Too many subnets to display grid. Use "Assign Subnet" button to pick specific prefix.</p>
          {opt.available_count > 0 && (
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              const net = subnetAddresses?.subnet?.subnet;
              if (net) {
                const base = net.split('/')[0];
                setSplitForm({ child_subnet: `${base}/${opt.prefix}`, description: '', vlan_id: '', gateway: '', customer_id: '', location: '', is_pool: false });
                setShowSplitCreate(subnetAddresses.subnet);
              }
            }}>
              <Plus className="h-3 w-3 mr-1" /> Create /{opt.prefix}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {opt.all_subnets.map(sn => (
            <SplitTile key={sn} sn={sn} opt={opt} subnetAddresses={subnetAddresses} setSplitForm={setSplitForm} setShowSplitCreate={setShowSplitCreate} />
          ))}
        </div>
      )}
    </div>
  );
}

function SplitOptionsCard({ splitOptions, subnetAddresses, setSplitForm, setShowSplitCreate }) {
  const options = splitOptions.options.filter(o => !(o.count > 4096 && !o.too_large_to_enumerate));
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><GitBranch className="h-4 w-4" /> Available Subnet Splits</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {options.map(opt => (
          <SplitOptionRow key={opt.prefix} opt={opt} subnetAddresses={subnetAddresses} setSplitForm={setSplitForm} setShowSplitCreate={setShowSplitCreate} />
        ))}
      </CardContent>
    </Card>
  );
}

function SubnetSubTabs({ subnetView, setSubnetView, physicalView, subnetAddresses, fetchPhysicalView, fetchSpaceMap, spaceMap }) {
  return (
    <div className="flex gap-1 border-b">
      <button
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subnetView === 'splits' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
        onClick={() => setSubnetView('splits')}
        data-testid="subview-splits-btn"
      >
        <GitBranch className="h-4 w-4 inline mr-1" /> Available Splits
      </button>
      <button
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subnetView === 'physical' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
        onClick={() => { setSubnetView('physical'); if (!physicalView && subnetAddresses?.subnet?.subnet_id) fetchPhysicalView(subnetAddresses.subnet.subnet_id); }}
        data-testid="subview-physical-btn"
      >
        <Grid3x3 className="h-4 w-4 inline mr-1" /> Physical View
      </button>
      <button
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subnetView === 'spacemap' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
        onClick={() => { setSubnetView('spacemap'); if (!spaceMap && subnetAddresses?.subnet?.subnet_id) fetchSpaceMap(subnetAddresses.subnet.subnet_id); }}
        data-testid="subview-spacemap-btn"
      >
        <Map className="h-4 w-4 inline mr-1" /> Space Map
      </button>
    </div>
  );
}

function SubnetInfoCard({ subnetAddresses }) {
  return (
    <Card><CardContent className="p-4">
      <UsageBar usage={subnetAddresses.usage} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
        <div><span className="text-slate-500">Network:</span> <span className="font-mono">{subnetAddresses.usage?.network}</span></div>
        <div><span className="text-slate-500">Broadcast:</span> <span className="font-mono">{subnetAddresses.usage?.broadcast}</span></div>
        <div><span className="text-slate-500">Netmask:</span> <span className="font-mono">{subnetAddresses.usage?.netmask}</span></div>
        <div><span className="text-slate-500">Gateway:</span> <span className="font-mono">{subnetAddresses.subnet?.gateway || '-'}</span></div>
      </div>
    </CardContent></Card>
  );
}

export function SubnetDetailView(props) {
  const {
    subnetAddresses, subnetView, setSubnetView, physicalView, fetchPhysicalView,
    splitOptions, selectedSubnetDetail,
    fetchSubnetAddresses, fetchSplitOptions, fetchSubnetDetail,
    setSplitForm, setShowSplitCreate, setShowAssignSubnet,
    setSubnetAddresses, setSelectedSubnetDetail, handleDeleteSubnet,
    spaceMap, fetchSpaceMap,
  } = props;

  const openChild = (id) => {
    fetchSubnetAddresses(id); fetchSplitOptions(id); fetchSubnetDetail(id); fetchPhysicalView(id); fetchSpaceMap && fetchSpaceMap(id); setSubnetView('splits');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setSubnetAddresses(null); setSelectedSubnetDetail(null); }}><ArrowLeft className="h-4 w-4" /></Button>
          <h3 className="text-lg font-semibold font-mono">{subnetAddresses.subnet?.subnet}</h3>
          <Badge variant="outline">{subnetAddresses.subnet?.description}</Badge>
        </div>
        <Button onClick={() => setShowAssignSubnet(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="assign-subnet-btn">
          <Plus className="h-4 w-4 mr-1" /> Assign Subnet
        </Button>
      </div>

      <SubnetInfoCard subnetAddresses={subnetAddresses} />

      <SubnetSubTabs
        subnetView={subnetView} setSubnetView={setSubnetView}
        physicalView={physicalView} subnetAddresses={subnetAddresses}
        fetchPhysicalView={fetchPhysicalView}
        spaceMap={spaceMap} fetchSpaceMap={fetchSpaceMap}
      />

      {subnetView === 'physical' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Grid3x3 className="h-4 w-4" /> Physical IP / Block Allocation</CardTitle></CardHeader>
          <CardContent>
            <PhysicalViewGrid data={physicalView} onDrillDown={openChild} />
          </CardContent>
        </Card>
      )}

      {subnetView === 'spacemap' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Map className="h-4 w-4" /> Space Map <span className="text-xs text-slate-500 font-normal">— every possible sub-block, live status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SpaceMapView data={spaceMap} />
          </CardContent>
        </Card>
      )}

      {subnetView === 'splits' && splitOptions && (
        <SplitOptionsCard splitOptions={splitOptions} subnetAddresses={subnetAddresses} setSplitForm={setSplitForm} setShowSplitCreate={setShowSplitCreate} />
      )}

      {subnetView === 'splits' && selectedSubnetDetail?.child_subnets?.length > 0 && (
        <ChildSubnetsCard childList={selectedSubnetDetail.child_subnets} openChild={openChild} handleDeleteSubnet={handleDeleteSubnet} />
      )}
    </div>
  );
}


// phpIPAM-style Space Map: one row per prefix level, every possible sub-block
// coloured by its live status. Backend endpoint: GET /api/ipam/subnets/{id}/space-map
export function SpaceMapView({ data }) {
  if (!data) {
    return <div className="text-sm text-slate-500 py-8 text-center">Loading space map…</div>;
  }
  if (!data.rows || data.rows.length === 0) {
    return <div className="text-sm text-slate-500 py-8 text-center">No sub-blocks to display (already at max prefix).</div>;
  }
  const cellClass = {
    free:      'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200',
    partial:   'bg-amber-100 text-amber-800 border-amber-300',
    blocked:   'bg-rose-200 text-rose-900 border-rose-400',
    allocated: 'bg-rose-300 text-rose-950 border-rose-500 font-semibold',
  };
  return (
    <div className="space-y-4" data-testid="space-map-view">
      <div className="text-xs text-slate-500 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 border border-emerald-400" /> Free</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 border border-amber-400" /> Partially used</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-200 border border-rose-400" /> Blocked</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-300 border border-rose-500" /> Allocated</span>
      </div>
      {data.rows.map((row) => (
        <div key={row.prefix} data-testid={`space-map-row-${row.prefix}`}>
          <div className="text-sm font-medium text-slate-700 mb-2">
            {row.free_count}/{row.total_count} free /{row.prefix} subnets
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
            {row.blocks.map((b) => (
              <div
                key={b.cidr}
                title={`${b.cidr} — ${b.status}`}
                data-testid={`space-map-cell-${b.cidr}`}
                className={`px-2 py-1.5 text-xs font-mono text-center rounded border transition-colors ${cellClass[b.status] || cellClass.free}`}
              >
                {b.cidr}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
